import React from 'react'
import { ImageSourcePropType } from 'react-native'
import {
  ATLDoubleButton,
  ATLIconizedText,
  ATLTextBody,
  ATLTextHead,
  ButtonTypeEnum,
  TextAlignmentEnum,
  ATLAtomIcon,
} from '@appsimples/atlantica-react-native'
import { atlTheme } from '../../atlTheme'
import { icons } from '../../constants/searchAssets'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { PresenceStatus } from './constants'
import styled from 'styled-components/native'
import { TextTwoColors } from '../../components/TextTwoColors'
import { screenWidth } from '../../constants/dimensions'
import { ButtonWithLoading } from '../../components/button-with-loading/ButtonWithLoading'

const Row = styled.View`
  flex-direction: row;
  padding: ${atlTheme.SpacingBase}px ${atlTheme.SpacingMarginal}px;
  width: ${screenWidth}px;
`
const IconContainer = styled.View`
  margin-right: ${atlTheme.SpacingBase / 2}px;
`
const AddressContainer = styled.View`
  margin-right: ${atlTheme.SpacingIconMarginRight}px;
  padding-right: ${atlTheme.SpacingIconMarginRight}px;
`

interface GenericAppointmentProps {
  title: string
  subtitle?: string
  constructionIcon: ImageSourcePropType
  constructionName: string
  date: string
  time: string
  address: string
  bottomMessage: string
  confirmAction: () => void
  cancelAction: () => void
  presenceStatus: PresenceStatus
  isLoading?: boolean
}

export const GenericAppointment: React.FC<GenericAppointmentProps> = props => {
  const {
    title,
    subtitle,
    constructionIcon,
    constructionName,
    time,
    date,
    address,
    bottomMessage,
    cancelAction,
    confirmAction,
    presenceStatus,
    isLoading,
  } = props
  const presenceWasConfirmed = presenceStatus === PresenceStatus.CONFIRMED
  const presenceWasCancelled = presenceStatus === PresenceStatus.CANCELLED

  return (
    <>
      <ATLTextHead textAlignment={TextAlignmentEnum.left}>{title}</ATLTextHead>
      {subtitle && <ATLTextBody labelBaseProps={{ multilineLimit: 2 }}>{subtitle}</ATLTextBody>}
      <ATLIconizedText icon={constructionIcon || icons.home} text={constructionName} />
      <Row>
        <IconContainer>
          <ATLAtomIcon source={icons.date} size={atlTheme.SizeIconBase} tintColor={atlTheme.ColorBrand2Default} />
        </IconContainer>
        <TextTwoColors firstText={formatMessage(messages.appointments_dateLabel)} secondText={date} />
      </Row>
      <Row>
        <IconContainer>
          <ATLAtomIcon source={icons.time} size={atlTheme.SizeIconBase} tintColor={atlTheme.ColorBrand2Default} />
        </IconContainer>
        <TextTwoColors firstText={formatMessage(messages.appointments_timeLabel)} secondText={`${time}h`} />
      </Row>
      <Row>
        <IconContainer>
          <ATLAtomIcon source={icons.location} size={atlTheme.SizeIconBase} tintColor={atlTheme.ColorBrand2Default} />
        </IconContainer>
        <AddressContainer>
          <TextTwoColors firstText={formatMessage(messages.appointments_addressLabel)} secondText={address} />
        </AddressContainer>
      </Row>
      <ATLTextBody
        textAlignment={TextAlignmentEnum.center}
        labelBaseProps={{ textColor: atlTheme.ColorMediumContrast, multilineLimit: 2 }}
      >
        {bottomMessage}
      </ATLTextBody>
      {isLoading ? (
        <ButtonWithLoading />
      ) : (
        <ATLDoubleButton
          leftTitle={formatMessage(messages.appointments_iWontGo)}
          leftOnPress={cancelAction}
          rightTitle={
            presenceWasConfirmed
              ? formatMessage(messages.appointments_confirmed)
              : formatMessage(messages.appointments_confirm)
          }
          rightOnPress={confirmAction}
          leftButtonProps={{ borderRadius: atlTheme.BorderRadius100 }}
          rightButtonProps={{ borderRadius: atlTheme.BorderRadius100 }}
          rightIcon={presenceWasConfirmed && icons.checkCircleIcon}
          rightType={presenceWasConfirmed ? ButtonTypeEnum.default : ButtonTypeEnum.outlined}
          leftType={presenceWasCancelled ? ButtonTypeEnum.default : ButtonTypeEnum.outlined}
        />
      )}
    </>
  )
}
